/* eslint-disable complexity */
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { selectOne } from 'reactApp/modules/selections/selections.actions';
import { getStorageConfig } from 'reactApp/modules/storage/storage.config';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { trashbinRestoreRequest } from 'reactApp/modules/trashbin/trashbin.module';
import { EFileError, EFileStatus } from 'reactApp/modules/uploadList/uploadList.model';
import { updateUploadFilesAction } from 'reactApp/modules/uploadList/uploadList.module';
import { DataListItemRowProps } from 'reactApp/ui/DataListItemRow/DataListItemRow.types';

import { useItemAuthor } from './useItemAuthors';

// eslint-disable-next-line max-lines-per-function
export const useRowProps = ({
    view,
    item,
    uploadingItem,
    handleOnCheckboxClick: onCheckboxClick,
    isSelected,
    isSelecting,
    isActive,
    tipData,
    id,
    storage,
    favoritesAvailable,
    onFavorites,
    onPublish,
    onDownload,
    isVirus,
    thumb,
}): DataListItemRowProps | undefined => {
    const dispatch = useDispatch();
    const itemId = item?.id;    
    const storageConfig = getStorageConfig(item.storage);
    const isFavoritesAvailable = storageConfig.favorites;

    const author = useItemAuthor(item);

    const onAccept = useCallback(
        (e) => {
            e.stopPropagation();
            e.preventDefault();
            dispatch(selectOne({ selectedIdx: itemId, storage }));
            toolbarActions.mount();
        },
        [dispatch, itemId, storage]
    );

    const onReject = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            dispatch(selectOne({ selectedIdx: itemId, storage }));
            toolbarActions.remove();
        },
        [dispatch, itemId, storage]
    );

    const onMount = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            dispatch(selectOne({ selectedIdx: itemId, storage }));
            toolbarActions.share();
        },
        [dispatch, itemId, storage]
    );

    const onRestore = useCallback(() => {
        dispatch(trashbinRestoreRequest({ ids: [itemId], from: 'datalist' }));
    }, [dispatch, itemId]);

    const onCancelUpload = useCallback(() => {
        if (!uploadingItem || (!uploadingItem.descriptorId && !uploadingItem.cloudPath)) {
            return;
        }

        dispatch(
            updateUploadFilesAction({
                cloudPath: uploadingItem.cloudPath,
                descriptorId: uploadingItem.descriptorId,
                status: EFileStatus.CANCEL,
                error: EFileError.CANCELLED_FILE,
                hideError: true,
            })
        );
    }, [dispatch, uploadingItem]);

    if (view !== 'list' || !item) {
        return;
    }

    const baseProps = {
        onCheckboxClick,
        id,
        storage: item.storage,
        tipData,
        isActive,
        isSelected,
        isSelecting,
    };

    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (item.storage) {
        case EStorageType.sharedIncoming: {
            return {
                ...baseProps,
                name: item.name,
                owner: item.owner,
                access: item.access,
                isMounted: !item.inviteToken,
                kind: item.kind,
                onAccept,
                onReject,
                onMount,
                size: item.size,
            };
        }
        case EStorageType.home: {
            return {
                ...baseProps,
                name: item.isFolder ? item.name : item.nameWithoutExt,
                isFolder: item.isFolder,
                ext: item.isFolder ? undefined : item.ext,
                kind: item.kind,
                mtime: item.isFolder ? undefined : item.mtime,
                size: item.size,
                isInFavorites: item.isInFavorites,
                isPublic: Boolean(item.weblink),
                isAutoDelete: item?.weblinkAutoDelete,
                publicExpires: item?.weblinkExpires,
                author,
                thumbUrl: thumb,
                favoritesAvailable,
                onFavorites,
                onPublish,
                onDownload,
                onCancelUpload,
                isVirus,
            };
        }
        case EStorageType.sharedLinks:
        case EStorageType.sharedAutodelete: {
            return {
                ...baseProps,
                name: item.isFolder ? item.name : item.nameWithoutExt,
                isFolder: item.isFolder,
                ext: item.isFolder ? undefined : item.ext,
                kind: item.kind,
                mtime: item.isFolder ? undefined : item.mtime,
                size: item.size,
                isPublic: Boolean(item.weblink),
                isAutoDelete: item?.weblinkAutoDelete,
                publicExpires: item?.weblinkExpires,
                thumbUrl: thumb,
                onPublish,
                onDownload,
                isVirus,
            };
        }
        case EStorageType.public: {
            return {
                ...baseProps,
                name: item.isFolder ? item.name : item.nameWithoutExt,
                isFolder: item.isFolder,
                ext: item.isFolder ? undefined : item.ext,
                kind: item.kind,
                author,
                mtime: item.isFolder ? undefined : item.mtime,
                size: item.size,
                thumbUrl: thumb,
                onDownload,
                isVirus,
            };
        }
        case EStorageType.favorites: {
            return {
                ...baseProps,
                name: item.nameWithoutExt,
                isFolder: item.isFolder,
                ext: item.ext,
                kind: item.kind,
                mtime: item.mtime,
                size: item.size,
                isInFavorites: true,
                author,
                isPublic: Boolean(item.weblink),
                isAutoDelete: item?.weblinkAutoDelete,
                publicExpires: item?.weblinkExpires,
                thumbUrl: thumb,
                favoritesAvailable: true,
                onFavorites,
                onPublish,
                onDownload,
                isVirus,
            };
        }
        case EStorageType.stock: {
            return {
                ...baseProps,
                name: item.isFolder ? item.name : item.nameWithoutExt,
                isFolder: item.isFolder,
                ext: item.isFolder ? undefined : item.ext,
                kind: item.kind,
                size: item.isFolder ? undefined : item.size,
                thumbUrl: thumb,
                onDownload,
                isVirus,
            };
        }
        case EStorageType.search: {
            return {
                ...baseProps,
                name: item.isFolder ? item.name : item.nameWithoutExt,
                isFolder: item.isFolder,
                ext: item.isFolder ? undefined : item.ext,
                kind: item.kind,
                isPublic: Boolean(item.weblink),
                isAutoDelete: item?.weblinkAutoDelete,
                publicExpires: item?.weblinkExpires,
                mtime: item.isFolder ? undefined : item.mtime,
                size: item.isFolder ? undefined : item.size,
                home: item.parentFolder,
                thumbUrl: thumb,
                onDownload,
                onPublish,
                isVirus,
                isInFavorites: item.isInFavorites,
                favoritesAvailable,
                onFavorites,
            };
        }
        case EStorageType.attaches: {
            return {
                ...baseProps,
                name: item.nameWithoutExt,
                isFolder: item.isFolder,
                ext: item.ext,
                mtime: item.mtime,
                size: item.size,
                thumbUrl: thumb,
                onDownload,
                subject: item.subject,
                kind: 'file',
                owner: {
                    name: item.authorName,
                    email: item.authorEmail,
                },
            };
        }
        case EStorageType.trashbin: {
            return {
                ...baseProps,
                name: item.isFolder ? item.name : item.nameWithoutExt,
                isFolder: item.isFolder,
                ext: item.isFolder ? undefined : item.ext,
                kind: item.kind,
                deletedAt: item.deletedAt,
                deletedFrom: item.deletedFrom,
                size: item.size,
                thumbUrl: thumb,
                binName: item.binName,
                onRestore,
            };
        }
        case EStorageType.feed: {
            return {
                ...baseProps,
                home: item.parent,
                name: item.nameWithoutExt,
                isFolder: false,
                ext: item.ext,
                kind: item.kind,
                mtime: item.mtime,
                size: item.size,
                isPublic: Boolean(item.weblink),
                thumbUrl: thumb,
                onPublish,
                onDownload,
                isVirus,
            };
        }
        case EStorageType.gallery: {
            return {
                ...baseProps,
                home: item.parent,
                name: item.nameWithoutExt,
                isFolder: false,
                ext: item.ext,
                kind: item.kind,
                mtime: item.mtime,
                size: item.size,
                isPublic: Boolean(item.weblink),
                thumbUrl: thumb,
                onPublish,
                onDownload,
                isVirus,
                favoritesAvailable: true,
                isInFavorites: item.isInFavorites,
                onFavorites,
            };
        }
        case EStorageType.albums: {
            return {
                ...baseProps,
                name: item.nameWithoutExt,
                isFolder: false,
                ext: item.ext,
                kind: item.kind,
                mtime: item.mtime,
                size: item.size,
                thumbUrl: thumb,
                onDownload,
                isVirus,
                favoritesAvailable: isFavoritesAvailable,
                isInFavorites: item.isInFavorites,
                onFavorites,
            };
        }
        case EStorageType.quotaCleaner: {
            return {
                ...baseProps,
                isFolder: item.isFolder,
                name: item.nameWithoutExt,
                kind: item.kind,
                ext: item.ext,
                isVirus: item.isVirus,
                thumbUrl: thumb,
                date: item.date,
                size: item.size,
                author: item.correspondents?.from?.[0],
                onDownload,
            };
        }
    }
};
